/**
 * guaranteeService - API calls for simulation-backed performance guarantee.
 *
 * Uses the same auth token + API_BASE_URL pattern as the rest of the app.
 */

import { getAuth } from 'firebase/auth';
import { API_BASE_URL } from '@vortx/shared-ui';

/**
 * Get the current user's auth token.
 * Throws if no user is logged in.
 */
async function getToken() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated — cannot call guarantee API');
  }
  return user.getIdToken();
}

/**
 * Start a guarantee simulation for a scenario.
 *
 * @param {string} projectId
 * @param {string} scenarioId
 * @returns {Promise<{ run_id: string, status: string }>}
 */
export async function startGuaranteeSimulation(projectId, scenarioId) {
  const token = await getToken();
  const res = await fetch(
    `${API_BASE_URL}/api/flux_ems/projects/${projectId}/guarantee-simulation`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ scenario_id: scenarioId }),
    }
  );

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || body.message || `Guarantee simulation failed (${res.status})`);
  }

  return res.json();
}

/**
 * Get the status / results of a guarantee simulation run.
 *
 * @param {string} projectId
 * @param {string} runId
 * @returns {Promise<{ status: string, progress?: object, results?: object, error?: string }>}
 */
export async function getGuaranteeRunStatus(projectId, runId) {
  const token = await getToken();
  const res = await fetch(
    `${API_BASE_URL}/api/flux_ems/projects/${projectId}/guarantee-simulation/${runId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || body.message || `Failed to fetch run status (${res.status})`);
  }

  return res.json();
}

/**
 * List guarantee simulation runs for a project.
 *
 * @param {string} projectId
 * @returns {Promise<{ runs: Array }>}
 */
export async function listGuaranteeRuns(projectId) {
  const token = await getToken();
  const res = await fetch(
    `${API_BASE_URL}/api/flux_ems/projects/${projectId}/guarantee-simulation`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || body.message || `Failed to list guarantee runs (${res.status})`);
  }

  return res.json();
}
